import { storage } from '@abyss/web/tools/storage';
import { Badge } from '@abyss/web/ui/Badge';
import { Grid } from '@abyss/web/ui/Grid';
import { Layout } from '@abyss/web/ui/Layout';
import React from 'react';

import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { Constants, PRACTITIONER_NAME_FOR_ANALYTICS } from '../../Constants';
import { ConstantsRoutes } from '../../ConstantsRoutes';
import { convertObjectToUrlParams, interleave } from '../../Utils';
import { DirectoryLink } from './DirectoryLink/DirectoryLink';
import { DirectoryListMobile } from './DirectoryListMobile/DirectoryListMobile';
import { renderDesktopItems, renderTabletItems } from './helper';
import {
  StyledDivider,
  layoutGroupStyles,
  letterBadgeStyles,
} from './PhysicianDirectory.styled';

export const matchSearchTextWithPhysician = (
  searchText,
  physicianList,
  navigate,
  currentPage,
  setDetailsStore,
  chipStore,
  { tablet, mobileScreen }
) => {
  const onClick = (physician) => {
    const detailsStoreValues = {
      providerId: physician.providerId,
    };
    setDetailsStore(detailsStoreValues);
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_ID,
      physician.providerId
    );
    adobeLinkTrackEvent({
      name: `${PRACTITIONER_NAME_FOR_ANALYTICS}`,
      location: `body:${Constants.FACILITY_DETAILS.PHYSICIAN_DIRECTORY}:page ${currentPage}`,
      type: 'internal',
      destinationUrl: '',
    });

    const urlParams = convertObjectToUrlParams(chipStore, detailsStoreValues);
    navigate(`${ConstantsRoutes.PROVIDER_DETAILS.path}${urlParams}`);
  };

  const search = (searchText ?? '').trim();
  let columnsCount = 3;
  if (mobileScreen) {
    columnsCount = 1;
  } else if (tablet) {
    columnsCount = 2;
  }
  const rows = Math.ceil(physicianList?.length / columnsCount);

  let physicianColumnsList: any[] = [];
  if (tablet) {
    physicianColumnsList = renderTabletItems(physicianList, rows);
  } else if (!mobileScreen) {
    physicianColumnsList = renderDesktopItems(physicianList, rows);
  }

  switch (true) {
    case mobileScreen:
      return physicianList?.map((physician) => (
        <DirectoryListMobile
          chipStore={chipStore}
          isTablet={false}
          key={physician?.providerId}
          linkOnClick={onClick}
          physician={physician}
          search={search}
        />
      ));
    case tablet:
      return (
        <Grid
          css={{ 'abyss-grid': { margin: 0, gap: '$lg' } }}
          style={{ display: 'flex', flexFlow: 'row' }}
        >
          {physicianColumnsList.map((physicians) => (
            <Grid
              css={{
                'abyss-grid': {
                  margin: 0,
                  flexDirection: 'column',
                  width: '50%',
                },
              }}
              key={`physicians-columns-${physicians[0]?.providerId}`}
            >
              <React.Fragment>
                {physicians?.map((physician) => (
                  <DirectoryListMobile
                    chipStore={chipStore}
                    isTablet
                    key={physician?.providerId}
                    linkOnClick={onClick}
                    physician={physician}
                    search={search}
                  />
                ))}
              </React.Fragment>
            </Grid>
          ))}
        </Grid>
      );
    default:
      return physicianColumnsList.map((physicians) => (
        <Grid
          css={{ 'abyss-grid': { margin: 0 } }}
          data-auto-testid="desktop-grid"
          data-testid="desktop-grid"
          key={`desktop-grid-${physicians[0]?.providerId}`}
        >
          {physicians?.map((physician) => (
            <Grid.Col
              css={{
                'abyss-grid-col': {
                  padding: '0',
                },
              }}
              key={physician?.providerId}
              span={{ xs: 12, md: 6, lg: 4 }}
            >
              <DirectoryLink
                chipStore={chipStore}
                linkOnClick={onClick}
                physician={physician}
                search={search}
              />
            </Grid.Col>
          ))}
        </Grid>
      ));
  }
};

export const getStartingLetterSections = (
  physicianDirectoryMapping,
  providerNameToSearch,
  navigate,
  currentPage,
  setDetailsStore,
  chipStore,
  { tablet, mobileScreen }
) =>
  interleave(
    Object.keys(physicianDirectoryMapping)
      .filter((letter) => physicianDirectoryMapping[letter].length > 0)
      .map((letter) => (
        <Layout.Group
          alignItems="left"
          css={layoutGroupStyles}
          key={letter}
          style={{ width: '100%' }}
        >
          <div style={{ width: '100%' }}>
            <Badge
              css={letterBadgeStyles}
              data-testid="physician-directory-badge"
              rounded
              variant="neutral"
            >
              {letter}
            </Badge>
            {matchSearchTextWithPhysician(
              providerNameToSearch,
              physicianDirectoryMapping[letter],
              navigate,
              currentPage,
              setDetailsStore,
              chipStore,
              { tablet, mobileScreen }
            )}
          </div>
        </Layout.Group>
      )),
    <StyledDivider color="$gray3" />
  );
